import { useState, useEffect, useRef } from 'react';
import { useApp } from '@context/AppContext';

/**
 * Floating emoji reaction renderer.
 * Watches game.reactions and animates emojis upward then fading.
 */
export default function RelayReactions() {
  const { state } = useApp();
  const { game } = state;
  const [floatingEmojis, setFloatingEmojis] = useState([]);
  const seenRef = useRef(new Set());

  // Watch for new reactions
  useEffect(() => {
    const reactions = game.reactions;
    if (!reactions || typeof reactions !== 'object') return;

    const entries = Object.entries(reactions);
    for (const [key, reaction] of entries) {
      if (seenRef.current.has(key)) continue;
      seenRef.current.add(key);

      // Add floating emoji
      const id = `${key}_${Date.now()}`;
      const xPos = 10 + Math.random() * 80; // random 10-90% from left

      setFloatingEmojis(prev => [
        ...prev.slice(-20), // keep max 20
        { id, emoji: reaction.emoji, x: xPos, createdAt: Date.now() },
      ]);
    }
  }, [game.reactions]);

  // Clean up old emojis (after 2.5 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setFloatingEmojis(prev => prev.filter(e => now - e.createdAt < 2500));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Clear seen refs when reactions are wiped (turn change)
  useEffect(() => {
    if (!game.reactions || Object.keys(game.reactions).length === 0) {
      seenRef.current.clear();
    }
  }, [game.reactions]);

  if (floatingEmojis.length === 0) return null;

  return (
    <div className="fixed inset-0 z-30 pointer-events-none overflow-hidden">
      {floatingEmojis.map((item) => (
        <div
          key={item.id}
          className="absolute text-3xl animate-[floatUp_2.5s_ease-out_forwards]"
          style={{ left: `${item.x}%`, bottom: '10%' }}
        >
          {item.emoji}
        </div>
      ))}

      {/* Keyframe animation */}
      <style>{`
        @keyframes floatUp {
          0% { transform: translateY(0) scale(1); opacity: 1; }
          50% { opacity: 1; }
          100% { transform: translateY(-300px) scale(0.5); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
